import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import {
  ImageUsageService,
  ImageUsageStats,
} from '../../../core/services/image-usage.service';
import { TranslationService, TranslationKeys } from '../../../core/services/translation.service';

@Component({
  selector: 'app-image-usage-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './image-usage-modal.component.html',
  styleUrls: ['./image-usage-modal.component.css'],
})
export class ImageUsageModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  currentStats: ImageUsageStats | null = null;
  showRemainingView = false;
  imagesUsedAtLastPurchase = 0;

  // Cache for calculations
  private _cachedImagePercentage: number | null = null;
  private _cachedUsageBarWidth: number | null = null;
  private _cachedUsageBarColor: string | null = null;
  private _cachedImagesText: string | null = null;

  private subscription?: Subscription;
  private translationSubscription?: Subscription;
  translations: TranslationKeys = {} as TranslationKeys;

  constructor(
    private imageUsageService: ImageUsageService,
    private translationService: TranslationService
  ) {}

  private clearCache() {
    this._cachedImagePercentage = null;
    this._cachedUsageBarWidth = null;
    this._cachedUsageBarColor = null;
    this._cachedImagesText = null;
  }

  ngOnInit(): void {
    console.log('🔧 ImageUsageModalComponent ngOnInit called');
    
    // Load translations
    this.translations = this.translationService.getAllTranslations();
    this.translationSubscription = this.translationService.currentLanguage$.subscribe(() => {
      this.translations = this.translationService.getAllTranslations();
      this.clearCache(); // Clear cache when language changes to update translated text
    });

    this.subscription = this.imageUsageService.usageStats$.subscribe(
      (stats) => {
        this.currentStats = stats;
        this.clearCache();

        if (stats) {
          this.showRemainingView = stats.showRemainingView || false;
          this.imagesUsedAtLastPurchase = stats.imagesUsedAtLastPurchase || 0;
        }
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('🔧 ImageUsageModalComponent ngOnChanges called:', changes);
    if (changes['isOpen'] && this.isOpen) {
      console.log('📊 Modal is opening, refreshing image data...');
      console.log('📱 Platform:', Capacitor.getPlatform());

      if (Capacitor.getPlatform() === 'ios') {
        console.log('📱 iOS detected, using force refresh...');
        this.imageUsageService.forceRefreshData();
        setTimeout(() => {
          this.currentStats = this.imageUsageService.getCurrentStats();
          this.clearCache();
        }, 200);
      } else {
        this.imageUsageService.loadImageUsageFromAPI();
        setTimeout(() => {
          this.currentStats = this.imageUsageService.getCurrentStats();
          this.clearCache();
        }, 100);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }

  closeModal(): void {
    this.close.emit();
  }

  getImagePercentage(): number {
    if (this._cachedImagePercentage !== null) {
      return this._cachedImagePercentage;
    }

    if (!this.currentStats) {
      this._cachedImagePercentage = 0;
      return 0;
    }

    const percentage =
      this.currentStats.imagesLimit > 0
        ? (this.currentStats.imagesUsed / this.currentStats.imagesLimit) * 100
        : 0;

    this._cachedImagePercentage = Math.min(percentage, 100);
    return this._cachedImagePercentage;
  }

  getUsageBarWidth(): number {
    if (this._cachedUsageBarWidth !== null) {
      return this._cachedUsageBarWidth;
    }

    this._cachedUsageBarWidth = this.getImagePercentage();
    return this._cachedUsageBarWidth;
  }

  getUsageBarColor(): string {
    if (this._cachedUsageBarColor !== null) {
      return this._cachedUsageBarColor;
    }

    const percentage = this.getImagePercentage();
    if (percentage >= 90) {
      this._cachedUsageBarColor = '#ef4444'; // red-500
    } else if (percentage >= 75) {
      this._cachedUsageBarColor = '#f59e0b'; // amber-500
    } else {
      this._cachedUsageBarColor = '#22c55e'; // green-500
    }

    return this._cachedUsageBarColor;
  }

  getImagesText(): string {
    if (this._cachedImagesText !== null) {
      return this._cachedImagesText;
    }

    if (!this.currentStats) {
      this._cachedImagesText = '';
      return '';
    }

    if (this.showRemainingView) {
      const remaining = Math.max(
        0,
        this.currentStats.imagesLimit - this.currentStats.imagesUsed
      );
      this._cachedImagesText = `${remaining.toLocaleString()} / ${this.currentStats.imagesLimit.toLocaleString()} ${this.translations.imagesRemaining || 'images remaining'}`;
    } else {
      this._cachedImagesText = `${this.currentStats.imagesUsed.toLocaleString()} / ${this.currentStats.imagesLimit.toLocaleString()} ${this.translations.imagesUsedText || 'images used'}`;
    }

    return this._cachedImagesText;
  }

  toggleView(): void {
    this.imageUsageService.toggleView();
    this.clearCache();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const currentLang = this.translationService.getCurrentLanguage();
    // Use appropriate locale based on current language
    const localeMap: { [key: string]: string } = {
      'en': 'en-US',
      'ko': 'ko-KR',
      'zh-CN': 'zh-CN',
      'zh-HK': 'zh-HK',
      'zh': 'zh-CN',
      'ky': 'ky-KG',
      'ru': 'ru-RU',
      'kk': 'kk-KZ',
      'tg': 'tg-TJ',
      'tk': 'tk-TM',
      'uz': 'uz-UZ',
      'fa': 'fa-IR',
      'es': 'es-ES',
    };
    const locale = localeMap[currentLang] || 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
